import { useEffect, useRef, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { AlertTriangle, RotateCcw, X } from 'lucide-react'
import { outcomeQueue } from './outcomeQueue'

/**
 * QUALIFICATION NON ENREGISTRÉE — le seul écran que produit l'avancée
 * optimiste, et il n'interrompt rien.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * CE QU'IL DOIT GARANTIR
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Depuis SCENE-2B, « Enregistrer » rend la main immédiatement : l'agent est
 * déjà en conversation avec la personne SUIVANTE quand l'échec d'écriture
 * arrive. Ce bandeau doit donc, à la lettre :
 *
 *  - **ne rien recouvrir et ne rien voler.** Ni modale, ni voile, ni piège à
 *    focus : une carte en bas d'écran, à gauche, hors du chemin de
 *    « Raccrocher » (bas-droite du panneau d'appel) ;
 *  - **survivre à la navigation.** Il est rendu par le provider, comme
 *    l'overlay d'entrant : un résultat perdu ne doit pas dépendre de l'écran
 *    sur lequel se trouve l'agent au moment où le réseau lâche ;
 *  - **proposer exactement deux gestes** : réessayer, ou renoncer. Rien à
 *    ressaisir — le contenu de la qualification est dans la file, intact.
 *
 * Le TOAST n'est émis qu'à la BASCULE vers l'échec (et non à chaque rendu) :
 * l'agent est au téléphone, une notification par seconde serait pire que le
 * défaut qu'elle signale.
 */
export function PendingOutcomeBanner() {
  const { t } = useTranslation('telephony')
  const state = useSyncExternalStore(
    outcomeQueue.subscribe,
    outcomeQueue.getSnapshot,
    outcomeQueue.getSnapshot,
  )

  const failed = state.jobs.filter((job) => job.status === 'failed')
  const previouslyFailed = useRef(0)

  useEffect(() => {
    const count = failed.length
    const grew = count > previouslyFailed.current
    previouslyFailed.current = count
    if (grew) toast.error(t('pendingOutcome.toast'), { duration: 6000 })
  }, [failed.length, t])

  if (failed.length === 0) return null

  return createPortal(
    <div className="fixed bottom-4 left-4 z-[9998] max-w-sm">
      <section
        role="status"
        data-testid="pending-outcome-banner"
        className="rounded-2xl border border-amber-400 bg-white p-3 shadow-xl dark:border-amber-500/50 dark:bg-gray-800"
      >
        <p className="flex items-start gap-2 text-sm font-semibold text-gray-900 dark:text-white">
          <AlertTriangle
            className="mt-0.5 h-4 w-4 shrink-0 text-amber-500"
            aria-hidden="true"
          />
          <span>
            {failed.length === 1
              ? t('pendingOutcome.title', { name: failed[0].label })
              : t('pendingOutcome.titleMany', { count: failed.length })}
          </span>
        </p>
        <p className="mt-1 pl-6 text-xs text-gray-600 dark:text-gray-300">
          {t('pendingOutcome.body')}
        </p>
        <div className="mt-3 flex gap-2 pl-6">
          <button
            type="button"
            onClick={() => outcomeQueue.retryFailed()}
            className="flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"
          >
            <RotateCcw className="h-4 w-4" aria-hidden="true" />
            {t('pendingOutcome.retry')}
          </button>
          <button
            type="button"
            onClick={() => outcomeQueue.discardFailed()}
            className="flex min-h-[44px] items-center justify-center gap-1 rounded-xl px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <X className="h-4 w-4" aria-hidden="true" />
            {t('pendingOutcome.discard')}
          </button>
        </div>
      </section>
    </div>,
    document.body,
  )
}
